const EMAIL_REGEX = /[^\s<>,]+@[^\s<>,]+\.[^\s<>,]+/;

function splitName(fullName) {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0 || (parts.length === 1 && parts[0] === '')) {
    return { firstName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: '', lastName: parts[0] };
  }
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(' ');
  return { firstName, lastName };
}

function parseLine(line) {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const emailMatch = trimmed.match(EMAIL_REGEX);
  const email = emailMatch ? emailMatch[0].trim() : null;

  let namePart = trimmed;
  if (email) {
    namePart = namePart.replace(EMAIL_REGEX, '');
    namePart = namePart.replace(/[<>,]/g, '').trim();
  }

  const { firstName, lastName } = splitName(namePart);
  const isSingleName = firstName === '' && lastName !== '';

  return {
    firstName,
    lastName,
    email,
    parseWarning: isSingleName ? 'Single name \u2014 assumed as last name' : null,
  };
}

function flagDuplicateEmails(entries) {
  const emailCounts = {};
  for (const entry of entries) {
    if (entry.email) {
      const lower = entry.email.toLowerCase();
      emailCounts[lower] = (emailCounts[lower] || 0) + 1;
    }
  }

  const seen = {};
  for (const entry of entries) {
    if (entry.email) {
      const lower = entry.email.toLowerCase();
      if (emailCounts[lower] > 1) {
        if (seen[lower]) {
          entry.parseWarning = `Duplicate email: ${entry.email}`;
        }
        seen[lower] = true;
      }
    }
  }

  return entries;
}

export function parseTextInput(text) {
  const lines = text.split('\n');
  const entries = [];
  for (const line of lines) {
    const entry = parseLine(line);
    if (entry) entries.push(entry);
  }
  return flagDuplicateEmails(entries);
}

function parseCSVRow(row) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < row.length && row[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

export function parseCSV(csvText) {
  const lines = csvText.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCSVRow(lines[0]).map(h => h.toLowerCase());
  const nameIdx = headers.indexOf('name');
  const firstIdx = headers.indexOf('first');
  const lastIdx = headers.indexOf('last');
  const emailIdx = headers.indexOf('email');

  const entries = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVRow(lines[i]);

    let firstName = '';
    let lastName = '';

    if (nameIdx !== -1) {
      const { firstName: f, lastName: l } = splitName(cols[nameIdx] || '');
      firstName = f;
      lastName = l;
    }
    if (firstIdx !== -1) firstName = cols[firstIdx] || '';
    if (lastIdx !== -1) lastName = cols[lastIdx] || '';

    const email = emailIdx !== -1 && cols[emailIdx] ? cols[emailIdx] : null;
    const isSingleName = firstName === '' && lastName !== '';

    entries.push({
      firstName,
      lastName,
      email,
      parseWarning: isSingleName ? 'Single name \u2014 assumed as last name' : null,
    });
  }

  return flagDuplicateEmails(entries);
}

export function groupByFamily(entries) {
  const familyMap = {};

  for (const entry of entries) {
    const key = entry.lastName.toLowerCase();
    if (!familyMap[key]) {
      familyMap[key] = {
        surname: entry.lastName,
        members: [],
      };
    }
    familyMap[key].members.push({
      firstName: entry.firstName,
      email: entry.email,
      parseWarning: entry.parseWarning,
    });
  }

  return Object.values(familyMap).sort((a, b) =>
    a.surname.localeCompare(b.surname)
  );
}
